import { Injectable, inject } from '@angular/core';
import moment from 'moment-timezone';
import { FsessionService } from '../session/fsession.service';

@Injectable({
  providedIn: 'root'
})
export class MomentService {
  private fse = inject(FsessionService);

  readonly cityUtcOffset = this.fse.optenerValor('utc_offset');
  readonly zonaHoraria = this.fse.optenerValor('zona_js');

  today = moment().utcOffset(this.cityUtcOffset);

  /**
   * Obtiene una instancia de moment con la zona horaria del municipio actual
   * si se le pasa una fecha la transforma.
   * @param date Objeto de fecha (opcional)
   * @returns Un objeto de moment con la zona horaria del municipio
   */
  withTimezone(date: moment.Moment | Date | string | null = null, format: string | null = null, keepLocal = false) {
    format = format == null ? "DD/MM/YYYY HH:mm:ss" : format;

    if (date) {
      return moment(date, format).utcOffset(this.cityUtcOffset, keepLocal);
    }

    return moment().utcOffset(this.cityUtcOffset);
  }

  asUTCTimezone(date: moment.Moment | Date | string | null = null, format: string | null = null, keepLocal = false) {
    format = format == null ? "DD/MM/YYYY HH:mm:ss" : format;

    if (date) {
      return moment.utc(date, format).utcOffset(this.cityUtcOffset, keepLocal);
    }
    return moment.utc().utcOffset(this.cityUtcOffset);
  }

  /**
   * Convierte una fecha local aplicando el offset de la ciudad y devuelve su versión en UTC real.
   * @example
   * const utc = this.localToUtc(myDate);
   * console.log(utc.toISOString());
   */
  localToUtc(date: moment.Moment | Date | string, format = 'YYYY-MM-DD HH:mm:ss') {
    const fechaStr = moment(date).local().format(format);
    const fechaUtc = moment
      .parseZone(`${fechaStr}${this.cityUtcOffset}`, `${format}Z`)
      .utc();
    return fechaUtc;
  }

  /**
   * Devuelve un fehcha como moment en la zona horaria local del municipio, aplicando el offset correspondiente.
   * Si no se pasa fecha, devuelve el momento actual.
   * @param date Fecha a convertir (opcional)
   * @param format Formato de parseo si date es string (por defecto DD/MM/YYYY HH:mm:ss)
   * @param keepLocal Si se pasa una fecha, si keepLocal es true se mantiene la hora local y se ajusta el offset, si es false se interpreta la fecha como UTC y se aplica el offset para convertirla a hora local (por defecto false)
   */
  asLocalTimezone(date: moment.Moment | Date | string | null = null, format: string | null = null, keepLocal = false) {
    format = format == null ? "DD/MM/YYYY HH:mm:ss" : format;
    
    if (date) {
      return moment(date, format).utcOffset(this.cityUtcOffset, keepLocal);
    }
    return moment().utcOffset(this.cityUtcOffset);
  }  

  /**
   * Devuelve la fecha actual en la zona horaria IANA del municipio.
   */
  now(): moment.Moment {
    return moment.tz(this.zonaHoraria);
  }

  /**
   * Convierte una fecha a la zona horaria IANA del municipio.
   * Si no se pasa fecha, devuelve el momento actual.
   * @param date Fecha a convertir (opcional)
   * @param format Formato de parseo si date es string (por defecto DD/MM/YYYY HH:mm:ss)
   */
  toZone(date: moment.Moment | Date | string | null = null, format = 'DD/MM/YYYY HH:mm:ss'): moment.Moment {
    if (date) {
      return moment.tz(date as any, format, this.zonaHoraria);
    }
    return moment.tz(this.zonaHoraria);
  }

  /**
   * Convierte una fecha UTC a la zona horaria IANA del municipio.
   * @param date Fecha en UTC
   * @param format Formato de parseo si date es string
   */
  fromUtcToZone(date: moment.Moment | Date | string | null = null, format = 'DD/MM/YYYY HH:mm:ss'): moment.Moment {
    if (date) {
      return moment.utc(date, format).tz(this.zonaHoraria);
    }
    return moment.utc().tz(this.zonaHoraria);
  }

  /**
   * Convierte una fecha en la zona horaria del municipio a UTC.
   * @param date Fecha local del municipio
   * @param format Formato de parseo si date es string
   */
  zoneToUtc(date: moment.Moment | Date | string, format = 'YYYY-MM-DD HH:mm:ss'): moment.Moment {
    return moment.tz(date as any, format, this.zonaHoraria).utc();
  }
}
